import { useAuth0 } from '@auth0/auth0-react';
import {
  useCreateSubcontractorTrailer,
  useDeleteSubcontractorTrailer,
  useLocations,
  useResources,
  useSetPartyAssignmentCap,
  useSitesWithMembers,
  useUpdateResource,
} from '@my/directory-api/hooks';
import { cn, showToast } from '@my/ui';
import { MyIIcon } from '@my/ui/icons';
import { ChevronDown, ChevronUp, Search } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { getCurrentOrganisationId } from '@/auth/currentOrg';
import {
  useReplaceLoadResources,
  useReplaceOrderResources,
  useReplaceOrderTaskResources,
  useReplaceTasksResources,
  useResequenceLoadStops,
  useResequenceOrderTasks,
} from '@my/tms-api/hooks';
import type { AssignmentSlotValue, OrderTask } from '@my/tms-api';
import { usePlanViewStore } from '@/stores/planView';
import {
  buildCascade,
  buildResourceJobIndex,
  cardAssignments,
  hasSplitAssignments,
  mergeAssignmentsDetailed,
  removeTaskSlot,
  type CardAssignment,
} from './lib/assignment';
import type { ResourceCardVM } from './lib/planResources';
import { normaliseDraft } from './lib/subcontractorTrailers';
import { AddSubcontractorTrailerModal } from './components/AddSubcontractorTrailerModal';
import { DriverGantt } from './components/DriverGantt';
import { JobQueue } from './components/JobQueue';
import { ResourceStrip } from './components/ResourceStrip';
import { TaskAllocationPanel } from './components/TaskAllocationPanel';
import { VehiclePickerPanel } from './components/VehiclePickerPanel';
import { MovementIntelligence } from './components/MovementIntelligence';
import { usePlanMovements } from './hooks/usePlanMovements';
import { usePlanResources } from './hooks/usePlanResources';
import { HorizonField } from '../views/HorizonField';
import {
  FILTER_ALL,
  matchesCargoFilter,
  matchesDirectionFilter,
  matchesSearch,
  isPlanSortKey,
  PLAN_SORT_OPTIONS,
  sortMovements,
  type GroupMode,
  type PlanMovement,
} from './lib/movements';

/**
 * Movement Assignment (Plan) — prototype.
 *
 * FE-only mock of the planning cockpit: a unified Job Queue (built Loads +
 * full-load Orders), a driver Gantt timeline, a driver roster, and the "my i"
 * intelligence panel (slides in from the right, same shell as Manifest). The
 * top bar matches the Manifest bar and uses the real depot list; everything
 * below is mock (`lib/mockData.ts`) — no API, no backend.
 */
export default function PlanPage() {
  const { user } = useAuth0();
  const orgId = useMemo(() => getCurrentOrganisationId(user), [user]);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [group, setGroup] = useState<GroupMode>('Flat');

  // Persisted view state (Gantt visibility, depot, horizon, queue filter).
  const ganttVisible = usePlanViewStore((s) => s.ganttVisible);
  const setGanttVisible = usePlanViewStore((s) => s.setGanttVisible);
  const resourcesCollapsed = usePlanViewStore((s) => s.resourcesCollapsed);
  const setResourcesCollapsed = usePlanViewStore((s) => s.setResourcesCollapsed);
  const depotId = usePlanViewStore((s) => s.depotId);
  const setDepot = usePlanViewStore((s) => s.setDepot);
  const extraDays = usePlanViewStore((s) => s.horizonDays);
  const setExtraDays = usePlanViewStore((s) => s.setHorizonDays);
  const queueFilter = usePlanViewStore((s) => s.queueFilter);
  const setQueueFilter = usePlanViewStore((s) => s.setQueueFilter);
  const cargoFilter = usePlanViewStore((s) => s.cargoFilter);
  const setCargoFilter = usePlanViewStore((s) => s.setCargoFilter);
  const directionFilter = usePlanViewStore((s) => s.directionFilter);
  const setDirectionFilter = usePlanViewStore((s) => s.setDirectionFilter);
  const search = usePlanViewStore((s) => s.search);
  const setSearch = usePlanViewStore((s) => s.setSearch);
  const showCompleted = usePlanViewStore((s) => s.showCompleted);
  const setShowCompleted = usePlanViewStore((s) => s.setShowCompleted);
  const sortKey = usePlanViewStore((s) => s.sortKey);
  const setSortKey = usePlanViewStore((s) => s.setSortKey);
  const sortDir = usePlanViewStore((s) => s.sortDir);
  const setSortDir = usePlanViewStore((s) => s.setSortDir);
  const resourcePool = usePlanViewStore((s) => s.resourcePool);
  const setResourcePool = usePlanViewStore((s) => s.setResourcePool);

  const GROUP_MODES: GroupMode[] = ['Flat', 'Driver', 'Direction'];

  // Resource pools, depot-filtered. Shared by the Gantt and the strip so both
  // always show the same resources.
  const {
    byPool,
    visiblePools,
    planFeatures,
    subcontractorOptions,
    equipmentTypeOptions,
    isLoading: resourcesLoading,
    isError: resourcesError,
  } = usePlanResources(orgId ?? null, depotId);
  // Fall back when the stored pool has nothing in it (e.g. after a depot switch).
  const activePool = visiblePools.includes(resourcePool) ? resourcePool : visiblePools[0];

  // Subcontractor assignment cap — persisted on the party via its own narrow
  // endpoint, so editing it here can't clobber a concurrent parties-screen edit.
  const setAssignmentCap = useSetPartyAssignmentCap();

  // Subcontractor trailers — registered from the strip's Add card.
  const [addTrailerOpen, setAddTrailerOpen] = useState(false);
  const createTrailer = useCreateSubcontractorTrailer();
  const deleteTrailer = useDeleteSubcontractorTrailer();

  /**
   * Re-sequence a job's stops from the allocation panel.
   *
   * <p>
   * Two different writes, because the two jobs order their stops differently. An
   * order's stops ARE its tasks, ordered by `OrderTask.Sequence`. A load's stops
   * can span several orders — each numbering its tasks from 1 — so a load's running
   * order lives on its own `LoadLine.Sequence` values instead, and rewriting the
   * tasks would be meaningless there.
   * </p>
   */
  const resequenceTasks = useResequenceOrderTasks();
  const resequenceLoadStops = useResequenceLoadStops();
  const reorderStops = useCallback(
    (movement: PlanMovement, taskIds: string[]) => {
      const onError = (err: Error) =>
        showToast({ type: 'error', message: `Couldn't re-sequence: ${err.message}` });

      if (movement.loadId) {
        // The BE turns the stop order into contiguous line sequences, keeping each
        // stop's lines together — a mapping the client shouldn't be guessing at.
        resequenceLoadStops.mutate(
          { loadId: movement.loadId, orderTaskIds: taskIds },
          { onError },
        );
        return;
      }

      if (movement.orderId) {
        resequenceTasks.mutate(
          {
            orderId: movement.orderId,
            // Contiguous 1..n. Gaps are legal but there's no reason to leave any,
            // and the endpoint requires distinct sequences covering every task.
            items: taskIds.map((taskId, index) => ({ taskId, sequence: index + 1 })),
          },
          { onError },
        );
      }
    },
    [resequenceTasks, resequenceLoadStops],
  );

  // Vehicle picker — opened from a driver card's "No vehicle" / "Choose vehicle".
  const [vehiclePickerFor, setVehiclePickerFor] = useState<string | null>(null);
  const updateResource = useUpdateResource();
  const resourcesQuery = useResources(orgId ?? null);
  const resourceById = useMemo(
    () => new Map((resourcesQuery.data ?? []).map((r) => [r.id, r])),
    [resourcesQuery.data],
  );
  const pickerDriverName = useMemo(() => {
    if (!vehiclePickerFor) return null;
    const driver = [...byPool.driverRoster, ...byPool.drivers].find(
      (d) => d.id === vehiclePickerFor,
    );
    return driver?.title ?? null;
  }, [vehiclePickerFor, byPool]);

  /** The vehicle the picker's driver currently has, for the unlink affordance. */
  const pickerCurrentVehicle = useMemo(() => {
    const driver = vehiclePickerFor ? resourceById.get(vehiclePickerFor) : undefined;
    const id = driver?.defaultVehicleResourceId;
    if (!id) return null;
    const v = resourceById.get(id);
    return v ? v.displayName || v.name : null;
  }, [vehiclePickerFor, resourceById]);

  /**
   * Set (or clear, with null) the driver's default vehicle. The update endpoint
   * replaces the whole resource, so the driver's other fields are echoed back
   * untouched alongside the new link.
   */
  const setDefaultVehicle = async (vehicleId: string | null) => {
    const driverId = vehiclePickerFor;
    const driver = driverId ? resourceById.get(driverId) : undefined;
    if (!driver) return;
    const vehicle = vehicleId ? byPool.vehicles.find((v) => v.id === vehicleId) : null;
    const driverLabel = driver.displayName || driver.name;
    setVehiclePickerFor(null);
    try {
      await updateResource.mutateAsync({
        id: driver.id,
        type: driver.type,
        name: driver.name,
        displayName: driver.displayName,
        mobileNumber: driver.mobileNumber,
        email: driver.email,
        reference: driver.reference,
        organisationEquipmentTypeId: driver.organisationEquipmentTypeId,
        homeSiteId: driver.homeSiteId,
        defaultVehicleResourceId: vehicleId,
        status: driver.status,
      });
      showToast({
        type: 'success',
        message: vehicleId
          ? `${vehicle?.title ?? 'Vehicle'} linked to ${driverLabel}.`
          : `Vehicle unlinked from ${driverLabel}.`,
      });
    } catch (err) {
      showToast({
        type: 'error',
        message:
          err instanceof Error
            ? err.message
            : `Failed to ${vehicleId ? 'link' : 'unlink'} the vehicle.`,
      });
    }
  };

  const {
    movements,
    assignments,
    orderNumberById,
    depotCountry,
    cargoOptions,
    directionOptions: directionOpts,
    isLoading: movementsLoading,
    isError: movementsError,
  } = usePlanMovements(orgId ?? null, depotId, showCompleted);

  // A stored selection can go stale — a cargo type gets retired, or today's jobs
  // no longer include that country pair. Fall back to All rather than silently
  // filtering everything out.
  const activeCargo = cargoOptions.includes(cargoFilter) ? cargoFilter : FILTER_ALL;
  const activeDirection = directionOpts.includes(directionFilter) ? directionFilter : FILTER_ALL;

  /**
   * Cargo + Direction (and the search) narrow the queue *before* it reaches the
   * Job Queue, so the status chips (and their counts) read as "of what I'm
   * looking at" rather than "of everything". The dropdowns' own counts stay
   * against the unfiltered set, so you can still see how big another option
   * would be before switching.
   */
  const scopedMovements = useMemo(
    () =>
      sortMovements(
        movements.filter(
          (m) =>
            matchesCargoFilter(m, activeCargo) &&
            matchesDirectionFilter(m, activeDirection) &&
            matchesSearch(m, search),
        ),
        // A stored key from an older build falls back to the default.
        isPlanSortKey(sortKey) ? sortKey : null,
        sortDir,
      ),
    [movements, activeCargo, activeDirection, search, sortKey, sortDir],
  );
  const selected = useMemo(
    () => movements.find((m) => m.id === selectedId) ?? null,
    [movements, selectedId],
  );

  /**
   * Which jobs each resource is currently on, so a trailer card can say it's busy.
   * Built from the unfiltered queue — a trailer doesn't become free because its job
   * was filtered out of view.
   */
  const jobsByResourceId = useMemo(() => buildResourceJobIndex(movements), [movements]);

  /**
   * Per-stop allocation panel. Opens after an assignment on a job with more than
   * two stops: a plain collect-and-deliver has nothing to decide, but once there
   * are intermediate stops, which resource covers which leg is a real question.
   * Display only for now.
   */
  const [allocationForId, setAllocationForId] = useState<string | null>(null);
  // Site names for the allocation panel's Location column. Same query the rest of
  // the page's location lookups use, so it's already cached.
  const locationsQuery = useLocations(orgId ?? null);
  const addressById = useMemo(
    () => new Map((locationsQuery.data ?? []).map((a) => [a.id, a])),
    [locationsQuery.data],
  );
  const allocationMovement = useMemo(
    () => movements.find((m) => m.id === allocationForId) ?? null,
    [movements, allocationForId],
  );
  const openAllocationIfMultiStop = useCallback((movement: PlanMovement) => {
    if (movement.tasks.length > 2) setAllocationForId(movement.id);
  }, []);

  /**
   * Select a queue row, and retarget the allocation panel when it's open.
   *
   * <p>
   * Chosen over locking the queue while the panel is up: the panel is a detail
   * view of a selected row, so following the selection is what a planner expects,
   * and disabling the grid would trap them into closing the panel to move on. When
   * the panel is closed this leaves it closed — selecting a row doesn't open it.
   * </p>
   */
  const selectMovement = useCallback((movementId: string) => {
    setSelectedId(movementId);
    setAllocationForId((current) => (current === null ? null : movementId));
  }, []);

  // ── Assignment ───────────────────────────────────────────────────────
  // Assigning a resource is a replace, so the write has to carry the whole set.
  // For an order movement that means reading its current assignments first; a
  // load movement already carries them inline on its LoadDto.
  const replaceOrderResources = useReplaceOrderResources();
  const replaceLoadResources = useReplaceLoadResources();

  /**
   * Assign a resource card to the selected movement.
   *
   * <p>
   * Merges rather than overwrites: picking a vehicle must not drop the driver
   * that's already on the job. The write is scoped to the whole order or the whole
   * load, so it refuses when the movement carries split assignments — flattening
   * someone's 4×25t split as a side effect of assigning a trailer would be silent
   * data loss. Those go through the per-task surface.
   * </p>
   */
  const assignToMovement = useCallback(
    (card: ResourceCardVM, selected: PlanMovement) => {
      // A driver brings their linked vehicle with them, unless a vehicle is
      // already on the job.
      const nexts = cardAssignments(card);
      if (nexts.length === 0) {
        showToast({ type: 'error', message: `${card.title} can't be assigned on its own.` });
        return;
      }

      const onError = (err: Error) =>
        showToast({ type: 'error', message: `Couldn't assign ${card.title}: ${err.message}` });
      // Name what actually landed — claiming the vehicle came too when it was
      // skipped would be worse than saying nothing.
      const describe = (applied: CardAssignment[]) =>
        applied
          .map((a) => a.referenceSnapshot)
          .filter((s): s is string => !!s)
          .join(' + ') || card.title;

      // Both branches read the movement's OWN assignments. Order rows carry them
      // from the org-wide fetch and loads from their DTO, so dropping onto any row
      // works — reading the *selected* row's set would assign against the wrong job.
      const current = selected.resources;

      if (selected.orderId) {
        if (hasSplitAssignments(current)) {
          showToast({
            type: 'error',
            message: 'This order has split assignments — edit them on the order instead.',
          });
          return;
        }
        const merged = mergeAssignmentsDetailed(current, nexts);
        replaceOrderResources.mutate(
          { orderId: selected.orderId, items: merged.items },
          {
            onSuccess: () => {
              showToast({
                type: 'success',
                message: `${describe(merged.applied)} → ${selected.jobNo}`,
              });
              openAllocationIfMultiStop(selected);
            },
            onError,
          },
        );
        return;
      }

      if (selected.loadId) {
        if (hasSplitAssignments(current)) {
          showToast({
            type: 'error',
            message: 'This manifest has split assignments — edit them on the order instead.',
          });
          return;
        }
        const merged = mergeAssignmentsDetailed(current, nexts);
        replaceLoadResources.mutate(
          { loadId: selected.loadId, items: merged.items },
          {
            onSuccess: () => {
              showToast({
                type: 'success',
                message: `${describe(merged.applied)} → ${selected.jobNo}`,
              });
              openAllocationIfMultiStop(selected);
            },
            onError,
          },
        );
      }
    },
    [replaceOrderResources, replaceLoadResources, openAllocationIfMultiStop],
  );

  /** The Assign button path — acts on whatever row is selected. */
  const handleAssign = useCallback(
    (card: ResourceCardVM) => {
      if (!selected) {
        showToast({ type: 'error', message: 'Pick a job in the queue first.' });
        return;
      }
      assignToMovement(card, selected);
    },
    [selected, assignToMovement],
  );

  /**
   * The drag-and-drop path — acts on the row dropped onto, which needn't be the
   * selected one. Selecting it as well keeps the intelligence panel in step with
   * what just changed.
   */
  const [draggedCard, setDraggedCard] = useState<ResourceCardVM | null>(null);
  const handleDropResource = useCallback(
    (movementId: string) => {
      const card = draggedCard;
      setDraggedCard(null);
      if (!card) return;
      const target = movements.find((m) => m.id === movementId);
      if (!target) return;
      selectMovement(movementId);
      assignToMovement(card, target);
    },
    [draggedCard, movements, assignToMovement, selectMovement],
  );

  /**
   * Drop a resource onto ONE stop in the allocation panel.
   *
   * <p>
   * This is what lets a job carry different resources on different legs — the
   * whole-job Assign covers every stop, this covers exactly the one dropped on.
   * Unlike the order/load-scoped writes it doesn't refuse when splits exist: the
   * per-task endpoint accepts cargo-item references, and `mergeTaskAssignments`
   * carries them through.
   * </p>
   */
  const replaceTaskResources = useReplaceOrderTaskResources();
  const replaceTasksResources = useReplaceTasksResources();
  const handleDropOnTask = useCallback(
    (_task: OrderTask, index: number, tasks: OrderTask[]) => {
      const card = draggedCard;
      setDraggedCard(null);
      if (!card) return;

      const nexts = cardAssignments(card);
      if (nexts.length === 0) {
        showToast({ type: 'error', message: `${card.title} can't be assigned on its own.` });
        return;
      }

      // Cascade: this stop and everything after it. Each stop merges against its
      // own rows, so the downstream ones keep their trailer and any split.
      const byTaskId = (taskId: string) => assignments.filter((a) => a.orderTaskId === taskId);
      const batch = buildCascade(tasks.map((t) => t.id), index, byTaskId, nexts);
      if (batch.length === 0) return;

      replaceTasksResources.mutate(
        { tasks: batch },
        {
          onSuccess: () =>
            showToast({
              type: 'success',
              message:
                batch.length === 1
                  ? `${card.title} → stop ${index + 1}`
                  : `${card.title} → stops ${index + 1}–${tasks.length}`,
            }),
          onError: (err) =>
            showToast({ type: 'error', message: `Couldn't assign ${card.title}: ${err.message}` }),
        },
      );
    },
    [draggedCard, assignments, replaceTasksResources],
  );

  const handleRemoveSlot = useCallback(
    (task: OrderTask, slot: AssignmentSlotValue) => {
      const current = assignments.filter((a) => a.orderTaskId === task.id);
      replaceTaskResources.mutate(
        {
          orderId: task.orderId,
          body: { orderTaskId: task.id, items: removeTaskSlot(current, slot) },
        },
        {
          onError: (err) =>
            showToast({ type: 'error', message: `Couldn't remove: ${err.message}` }),
        },
      );
    },
    [assignments, replaceTaskResources],
  );

  // Real depot list — organisation Sites, name-sorted, auto-pick the first.
  const sitesQuery = useSitesWithMembers(orgId);
  const sites = useMemo(
    () => (sitesQuery.data ?? []).slice().sort((a, b) => a.name.localeCompare(b.name)),
    [sitesQuery.data],
  );
  useEffect(() => {
    const first = sites[0];
    if (!first) return;
    if (!depotId || !sites.some((s) => s.id === depotId)) setDepot(first.id);
  }, [sites, depotId, setDepot]);

  return (
    <div className="relative flex h-full min-h-0 flex-col overflow-hidden">
      {/* Top bar — the same panel as the Orders screen's filter row: no line
          under it, and the Job Queue header lands where the Orders grid does
          (10px here + the Job Queue box's own border = the Orders gap). */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-t-lg border border-b-0 border-slate-200 bg-white px-3 pb-2.5 pt-1">
        <Field label="Depot">
          <select
            value={depotId ?? ''}
            onChange={(e) => setDepot(e.target.value || null)}
            className={FILTER_SELECT}
          >
            {sites.length === 0 && <option value="">No sites</option>}
            {sites.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </Field>

        <HorizonField value={extraDays} onChange={setExtraDays} compact />

        {/* The org switch removes the control, not just the timeline. A toggle
            that stays put and does nothing is worse than no toggle: it reads as
            a bug, and the planner's own stored `ganttVisible` preference is
            left untouched underneath, so restoring the feature restores their
            last choice. */}
        {planFeatures.gantt && (
          <Field label="Gantt">
            <button
              type="button"
              role="switch"
              aria-checked={ganttVisible}
              aria-label="Toggle Gantt timeline"
              onClick={() => setGanttVisible(!ganttVisible)}
              className={cn(
                'relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200 ease-in-out',
                ganttVisible ? 'bg-blue-600' : 'bg-slate-300',
              )}
            >
              <span
                className={cn(
                  'inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform duration-200 ease-in-out',
                  ganttVisible ? 'translate-x-4' : 'translate-x-0.5',
                )}
              />
            </button>
          </Field>
        )}

        <Field label="Group">
          <div className="flex items-center gap-1.5">
            {GROUP_MODES.map((g) => (
              <button
                key={g}
                type="button"
                onClick={() => setGroup(g)}
                className={planChipClass(g === group)}
              >
                {g}
              </button>
            ))}
          </div>
        </Field>

        <Field label="Cargo">
          <select
            value={activeCargo}
            onChange={(e) => setCargoFilter(e.target.value)}
            className={FILTER_SELECT}
          >
            <option value={FILTER_ALL}>All ({movements.length})</option>
            {cargoOptions.map((c) => (
              <option key={c} value={c}>
                {c} ({movements.filter((m) => matchesCargoFilter(m, c)).length})
              </option>
            ))}
          </select>
        </Field>

        <Field label="Direction">
          <select
            value={activeDirection}
            onChange={(e) => setDirectionFilter(e.target.value)}
            className={FILTER_SELECT}
          >
            <option value={FILTER_ALL}>All ({movements.length})</option>
            {directionOpts.map((d) => (
              <option key={d} value={d}>
                {d} ({movements.filter((m) => matchesDirectionFilter(m, d)).length})
              </option>
            ))}
          </select>
        </Field>

        {/* Sort, as on the Orders screen — grouping keeps it within each section. */}
        <div className="ml-auto flex items-center gap-1">
          <label
            htmlFor="plan-sort"
            className="mr-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400"
          >
            Sort
          </label>
          <select
            id="plan-sort"
            value={sortKey ?? ''}
            onChange={(e) => setSortKey(isPlanSortKey(e.target.value) ? e.target.value : null)}
            className={FILTER_SELECT}
          >
            {PLAN_SORT_OPTIONS.map((o) => (
              <option key={o.key ?? 'newest'} value={o.key ?? ''}>
                {o.label}
              </option>
            ))}
          </select>
          {/* Newest is always newest first, so it has no direction. */}
          <button
            type="button"
            disabled={!sortKey}
            onClick={() => setSortDir(sortDir === 'asc' ? 'desc' : 'asc')}
            title={sortDir === 'asc' ? 'Ascending — click for descending' : 'Descending — click for ascending'}
            aria-label={sortDir === 'asc' ? 'Sort ascending' : 'Sort descending'}
            className="inline-flex items-center gap-0.5 rounded-md border border-slate-300 bg-white px-1.5 py-1 text-[11px] text-slate-700 hover:border-slate-500 disabled:cursor-default disabled:opacity-40"
          >
            {sortDir === 'asc' ? (
              <ChevronUp className="h-3 w-3" aria-hidden="true" />
            ) : (
              <ChevronDown className="h-3 w-3" aria-hidden="true" />
            )}
            {sortDir === 'asc' ? 'Asc' : 'Desc'}
          </button>
        </div>

        {/* Search, as on the Orders screen — narrows the Job Queue. */}
        <div className="relative">
          <Search
            className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400"
            aria-hidden="true"
          />
          <input
            type="text"
            placeholder="Search jobs…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search jobs"
            className="w-52 rounded-full border border-slate-300 bg-white py-1.5 pl-8 pr-3 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
        </div>

        <span className="inline-flex items-center gap-3 text-xs text-slate-500">
          <LiveClock />
          {/* MyI presence — opens the movement-intelligence overlay. */}
          <button
            type="button"
            onClick={() => setAssistantOpen(true)}
            aria-label="Open movement intelligence"
            className={cn(
              'shrink-0 rounded-full transition-colors hover:bg-slate-100',
              assistantOpen && 'opacity-40',
            )}
          >
            <MyIIcon className="h-7 w-7 animate-pulse" />
          </button>
        </span>
      </div>

      {/* Body — full width; the intelligence panel overlays it when open. The
          bar above carries the gap down to the Job Queue. */}
      <div
        className={cn(
          'flex min-h-0 flex-1 flex-col gap-3 overflow-hidden',
          // The folded resources handle sits on the very bottom edge.
          !resourcesCollapsed && 'pb-2',
        )}
      >
        <JobQueue
          organisationId={orgId ?? null}
          movements={scopedMovements}
          isLoading={movementsLoading}
          isError={movementsError}
          selectedId={selectedId}
          onSelect={selectMovement}
          filter={queueFilter}
          onFilterChange={setQueueFilter}
          showCompleted={showCompleted}
          onShowCompletedChange={setShowCompleted}
          group={group}
          depotCountry={depotCountry}
          addressById={addressById}
          dragging={draggedCard !== null}
          onDropResource={handleDropResource}
          // An explicit click opens regardless of stop count — unlike the
          // automatic open after an assignment, the planner asked for it.
          onOpenAllocation={setAllocationForId}
          expandedId={allocationForId}
          renderExpanded={(m) => (
            <TaskAllocationPanel
              movement={m}
              assignments={assignments}
              addressById={addressById}
              orderNumberById={orderNumberById}
              dragging={draggedCard !== null}
              onDropOnTask={handleDropOnTask}
              onReorder={(taskIds) => reorderStops(m, taskIds)}
              onRemoveSlot={handleRemoveSlot}
              reordering={resequenceTasks.isPending || resequenceLoadStops.isPending}
              mutating={replaceTaskResources.isPending || replaceTasksResources.isPending}
              onClose={() => setAllocationForId(null)}
            />
          )}
        />
        {/* The allocation panel takes the Gantt's place rather than stacking on
            top of it — both want the same vertical room, and the two aren't
            useful at once: one is per-stop detail for a single job, the other is
            a whole-fleet timeline. The Gantt toggle is left untouched, so closing
            the panel restores whatever the planner had chosen. */}
        {planFeatures.gantt && ganttVisible && !allocationMovement && (
          <DriverGantt
            resources={activePool ? byPool[activePool] : []}
            activePool={activePool}
            isLoading={resourcesLoading}
          />
        )}
        <ResourceStrip
          byPool={byPool}
          visiblePools={visiblePools}
          activePool={activePool}
          onPoolChange={setResourcePool}
          isLoading={resourcesLoading}
          isError={resourcesError}
          onSelectVehicle={setVehiclePickerFor}
          onAssignmentCapChange={(partyId, next) => {
            if (!orgId) return;
            setAssignmentCap.mutate(
              { partyId, organisationId: orgId, maxConcurrentAssignments: next },
              {
                onError: (err) =>
                  showToast({ type: 'error', message: `Couldn't save the cap: ${err.message}` }),
              },
            );
          }}
          jobsByResourceId={jobsByResourceId}
          onAssign={handleAssign}
          onDragStartCard={setDraggedCard}
          onDragEndCard={() => setDraggedCard(null)}
          assigning={replaceOrderResources.isPending || replaceLoadResources.isPending}
          collapsed={resourcesCollapsed}
          onCollapsedChange={setResourcesCollapsed}
          onAddSubcontractorTrailer={() => setAddTrailerOpen(true)}
          onRemoveSubcontractorTrailer={(trailerId, unitName) => {
            if (!orgId) return;
            deleteTrailer.mutate(
              { organisationId: orgId, trailerId },
              {
                onSuccess: () =>
                  showToast({ type: 'success', message: `${unitName} removed.` }),
                onError: (err) =>
                  showToast({ type: 'error', message: `Couldn't remove ${unitName}: ${err.message}` }),
              },
            );
          }}
        />
        <AddSubcontractorTrailerModal
          open={addTrailerOpen}
          subcontractorOptions={subcontractorOptions}
          equipmentTypeOptions={equipmentTypeOptions}
          saving={createTrailer.isPending}
          onCancel={() => setAddTrailerOpen(false)}
          onSave={(draft) => {
            if (!orgId) return;
            const body = normaliseDraft(draft);
            createTrailer.mutate(
              { organisationId: orgId, ...body },
              {
                onSuccess: () => {
                  setAddTrailerOpen(false);
                  showToast({ type: 'success', message: `${body.unitName} added.` });
                },
                onError: (err) =>
                  showToast({ type: 'error', message: `Couldn't add the trailer: ${err.message}` }),
              },
            );
          }}
        />
      </div>

      {/* Vehicle picker sits below the my i rail (z-30 vs z-40). */}
      <VehiclePickerPanel
        open={vehiclePickerFor !== null}
        driverName={pickerDriverName}
        vehicles={byPool.vehicles}
        currentVehicleName={pickerCurrentVehicle}
        onClose={() => setVehiclePickerFor(null)}
        onLink={(vehicleId) => void setDefaultVehicle(vehicleId)}
        onUnlink={() => void setDefaultVehicle(null)}
      />

      <MovementIntelligence
        open={assistantOpen}
        onClose={() => setAssistantOpen(false)}
        selected={selected}
      />
    </div>
  );
}

/** Live wall clock — real current date + time, ticking every second. */
function LiveClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  const date = now.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
  const time = now.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="h-2 w-2 rounded-full bg-emerald-500" />
      <span className="tabular-nums">
        {date} · {time}
      </span>
    </span>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
        {label}
      </span>
      {children}
    </div>
  );
}

/** The bar's dropdowns — the Orders screen's Sort select. */
const FILTER_SELECT =
  'rounded-md border border-slate-300 bg-white px-1.5 py-1 text-[11px] text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-400';

/** The Orders screen's filter chips. */
function planChipClass(active: boolean): string {
  return cn(
    'rounded-full border px-2.5 py-0.5 text-[10px] font-medium transition-colors',
    active
      ? 'border-slate-700 bg-slate-700 text-white'
      : 'border-slate-300 bg-white text-slate-500 hover:border-slate-500 hover:text-slate-700',
  );
}
